import { format } from "date-fns";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { MoreVertical, Pencil, Trash2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../../../components/ui/dialog";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../../../../components/ui/radio-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import InlineFieldPicker from "../../../../../shared/ui/dropdown/InlineFieldPicker";
import { formatFieldValue } from "../../../../../utils/formatFieldValue";

interface Props {
  title: string;
  data: any;
  type?: string;
  description?: string;
  loading?: boolean;
  deleteHandler?: any;
  id?: any;
  editTitleHandler?: any;
  updateMetricHandler?: any;
  metricsList?: any[];
  aggregator?: string;
  fieldDef?: { data_type?: string; display_format?: string; display_rules?: any; field_type?: string };
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    let formattedLabel = label;
    try {
      if (typeof label === "string" && label.includes("T")) {
        formattedLabel = format(new Date(label), "yyyy-MM-dd");
      }
    } catch {
      formattedLabel = label;
    }

    return (
      <div className="bg-gray-700 px-3 py-2 rounded-md">
        <p className="text-xs text-gray-400">{formattedLabel}</p>
        <p className="text-xs text-white">{`Count: ${payload[0].value}`}</p>
      </div>
    );
  }
  return null;
};

const displayTypeOptions: { value: string; label: string; hint: string }[] = [
  { value: "value", label: "Value", hint: "Shows the current field value (text, date, etc.)" },
  { value: "stat", label: "Stat Only", hint: "Shows a number with percentage change over time" },
  { value: "line", label: "Line Chart", hint: "Displays a line chart of values over time" },
  { value: "bar", label: "Bar Chart", hint: "Displays a bar chart of values over time" },
];

const CustomChart = ({
  title,
  data,
  type = "area",
  description,
  loading = false,
  deleteHandler,
  id,
  editTitleHandler,
  updateMetricHandler,
  metricsList = [],
  aggregator,
  fieldDef,
}: Props) => {
  const [heading, setHeading] = useState<string>(title);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<any>(null);
  const [selectedChartType, setSelectedChartType] = useState<string>(type);

  // Split metricsList into traits and metrics for InlineFieldPicker
  const traitItems = useMemo(
    () => metricsList.filter((item: any) => item.attribute_type !== "metrics"),
    [metricsList],
  );
  const metricItems = useMemo(
    () => metricsList.filter((item: any) => item.attribute_type === "metrics"),
    [metricsList],
  );

  // Sync heading when title prop changes
  useEffect(() => { setHeading(title); }, [title]);

  const processChartData = () => {
    if (Array.isArray(data)) {
      return data
        .map((item: any) => {
          if (!item || typeof item !== "object") return null;
          try {
            return {
              name: format(new Date(item.date), "MMM d"),
              count: typeof item.count === "number" ? item.count : 0,
              date: item.date,
            };
          } catch {
            return null;
          }
        })
        .filter(Boolean)
        .reverse();
    }

    if (data && typeof data === "object") {
      if (data.histogram_values && Array.isArray(data.histogram_values)) {
        return data.histogram_values
          .map((item: any) => {
            if (!item || typeof item !== "object") return null;
            try {
              return {
                name: format(new Date(item.date), "MMM d"),
                count: typeof item.count === "number" ? item.count : 0,
                date: item.date,
              };
            } catch {
              return null;
            }
          })
          .filter(Boolean)
          .reverse();
      }

      const keys = Object.keys(data);
      const nestedData = keys
        .filter((key) => data[key]?.histogram_values && Array.isArray(data[key].histogram_values))
        .flatMap((key) => data[key].histogram_values);

      if (nestedData.length > 0) {
        return nestedData
          .map((item: any) => {
            if (!item || typeof item !== "object") return null;
            try {
              return {
                name: format(new Date(item.date), "MMM d"),
                count: typeof item.count === "number" ? item.count : 0,
                date: item.date,
              };
            } catch {
              return null;
            }
          })
          .filter(Boolean)
          .reverse();
      }
    }

    return [];
  };

  const transformedData = processChartData();

  // ── Value display type: show formatted field value ──
  const isValueType = type === "value";
  const isStatOnly = type === "stat";
  const isChartType = type === "line" || type === "bar";

  // Extract total_count and percentage_change
  const extractTotalCount = () => {
    if (data && typeof data === "object") {
      if ("total_count" in data) return data.total_count;
      if ("current_value" in data) return data.current_value;
      const firstKey = Object.keys(data)[0];
      const firstValue = firstKey ? data[firstKey] : null;
      if (firstValue && typeof firstValue === "object" && "total_count" in firstValue) return firstValue.total_count;
    }
    return "N/A";
  };

  const extractPercentageChange = () => {
    if (data && typeof data === "object") {
      if ("percentage_change" in data) return data.percentage_change;
      const firstKey = Object.keys(data)[0];
      const firstValue = firstKey ? data[firstKey] : null;
      if (firstValue && typeof firstValue === "object" && "percentage_change" in firstValue) return firstValue.percentage_change;
    }
    return null;
  };

  const totalCount = extractTotalCount();
  const percentChange = extractPercentageChange();

  const changeIcon =
    percentChange === null || percentChange === undefined || percentChange === "N/A" ? null :
    percentChange > 0 ? <TrendingUp className="h-3 w-3" /> :
    percentChange < 0 ? <TrendingDown className="h-3 w-3" /> :
    <Minus className="h-3 w-3" />;

  const changeColor =
    percentChange === null || percentChange === undefined || percentChange === "N/A"
      ? "text-gray-500"
      : percentChange > 0
      ? "text-green-600"
      : percentChange < 0
      ? "text-red-500"
      : "text-gray-500";

  // ── Edit modal helpers ──
  const handleOpenModal = () => {
    if (aggregator && metricsList?.length > 0) {
      const currentMetric = metricsList.find((m) => m.name === aggregator);
      setSelectedMetric(currentMetric || null);
    }
    setHeading(title);
    setSelectedChartType(type);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setHeading(title);
    setSelectedMetric(null);
    setSelectedChartType(type);
    setModalOpen(false);
  };

  const hasChanges = () => {
    const titleChanged = title !== heading;
    const metricChanged = selectedMetric && aggregator !== selectedMetric.name;
    const chartTypeChanged = type !== selectedChartType;
    return titleChanged || metricChanged || chartTypeChanged;
  };

  const allowedTypes = selectedMetric?.pinnable_as || ["line", "bar", "stat"];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      {/* Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) handleCloseModal(); }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-sm">Edit Pinned Metric</DialogTitle>
            <DialogDescription>Update your metric view</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pt-1 pr-1">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Title</Label>
              <Input
                value={heading}
                onChange={(e) => setHeading(e.target.value)}
                placeholder="e.g. Monthly Revenue"
                className="h-8 text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                Select Property <span className="text-red-500">*</span>
              </Label>
              <InlineFieldPicker
                list={traitItems}
                metricsList={metricItems}
                selected={selectedMetric?.name || null}
                onSelect={setSelectedMetric}
                type="Accounts"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Display Type</Label>
              <p className="text-[11px] text-muted-foreground">
                Available display types depend on the selected field.
              </p>
              <RadioGroup
                value={selectedChartType}
                onValueChange={setSelectedChartType}
                className="flex flex-wrap gap-3 pt-1"
              >
                {displayTypeOptions.map((opt) => {
                  const disabled = !allowedTypes.includes(opt.value);
                  return (
                    <div key={opt.value} className="relative group flex items-center gap-1.5">
                      <RadioGroupItem
                        value={opt.value}
                        id={`edit-${id}-${opt.value}`}
                        disabled={disabled}
                      />
                      <Label
                        htmlFor={`edit-${id}-${opt.value}`}
                        className={`text-xs font-normal cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        {opt.label}
                      </Label>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-48 bg-gray-800 text-white text-[10px] rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-20 pointer-events-none text-center">
                        {opt.hint}
                      </div>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-border mt-2">
            <button
              type="button"
              onClick={handleCloseModal}
              className="h-7 px-3 text-xs font-medium border border-gray-200 rounded-md bg-white text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!heading || !selectedMetric || !hasChanges()}
              onClick={() => {
                const titleChanged = title !== heading;
                const metricChanged = selectedMetric && aggregator !== selectedMetric.name;
                const chartTypeChanged = type !== selectedChartType;
                if (titleChanged && !metricChanged && !chartTypeChanged && editTitleHandler) {
                  editTitleHandler(id, heading);
                } else if ((metricChanged || chartTypeChanged) && updateMetricHandler) {
                  updateMetricHandler(id, selectedMetric!.name, heading, selectedChartType);
                }
                setModalOpen(false);
              }}
              className="h-7 px-3 text-xs font-medium rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Save Changes
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Card Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 truncate max-w-[180px]">{heading}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="text-gray-400 hover:text-gray-700 transition-colors">
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[140px]">
            {editTitleHandler && (
              <DropdownMenuItem onClick={handleOpenModal} className="text-xs gap-2">
                <Pencil className="h-3 w-3" /> Edit
              </DropdownMenuItem>
            )}
            {deleteHandler && (
              <DropdownMenuItem onClick={() => deleteHandler(id)} className="text-xs gap-2 text-red-600 focus:text-red-600">
                <Trash2 className="h-3 w-3" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ── Value display ── */}
      {isValueType ? (
        <div className="flex items-center min-h-[60px]">
          <div className="text-lg font-semibold text-gray-900">
            {formatFieldValue(data?.current_value ?? totalCount, fieldDef)}
          </div>
        </div>
      ) : (
        <>
          {/* Value + Change */}
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-semibold text-gray-900">
              {fieldDef ? formatFieldValue(totalCount, fieldDef) : totalCount}
            </span>
            {percentChange !== null && percentChange !== undefined && percentChange !== "N/A" ? (
              <span className={`flex items-center gap-0.5 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 ${changeColor}`}>
                {changeIcon}
                {percentChange > 0 ? "+" : ""}{percentChange}%
              </span>
            ) : (
              <span className="text-[11px] font-medium text-gray-500 px-1.5 py-0.5 rounded-full bg-gray-100">
                — N/A
              </span>
            )}
          </div>

          {/* Chart */}
          {isStatOnly ? null : isChartType && (loading ? (
            <div className="flex items-center justify-center h-[80px] text-gray-400 text-xs">
              Loading...
            </div>
          ) : transformedData.length > 0 ? (
            <>
              <div className="h-[80px] -mx-1">
                <ResponsiveContainer width="100%" height="100%">
                  {type === "bar" ? (
                    <BarChart data={transformedData}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                      <Tooltip content={CustomTooltip} />
                      <Bar dataKey="count" fill="#8267E6" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  ) : (
                    <AreaChart data={transformedData}>
                      <defs>
                        <linearGradient id={`colorCount-${id}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8267E6" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#8267E6" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                      <Tooltip content={CustomTooltip} />
                      <Area type="monotone" dataKey="count" stroke="#8267E6" fill={`url(#colorCount-${id})`} strokeWidth={1.5} dot={false} />
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              </div>
              {/* Date range footer */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-400">{transformedData[0]?.name}</span>
                <span className="text-[10px] text-gray-400">{transformedData[transformedData.length - 1]?.name}</span>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[80px] text-gray-400 text-xs">
              No data available
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default CustomChart;
